import React from 'react';
import { Flame, Sun, Thermometer, CloudSun, Ban } from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;

export const LEVEL_META = {
  brulant: { label: 'Brûlant', pct: '99 %', hint: 'Ça signe', icon: Flame, bg: '#FBE4E2', fg: '#A1281D', bar: '#D1361F' },
  tres_chaud: { label: 'Très chaud', pct: '90 %', hint: 'Le client doit vérifier quelques points', icon: Flame, bg: '#FDE3D1', fg: '#8A3206', bar: '#F26A1B' },
  chaud: { label: 'Chaud', pct: '75 %', hint: 'En concurrence, mais je sens le dossier pour Wasabi', icon: Sun, bg: '#FBEFD5', fg: '#7A4F0E', bar: '#E3A21A' },
  tiede: { label: 'Tiède', pct: '50/50', hint: 'Rien n’est joué', icon: CloudSun, bg: '#E6EDF4', fg: '#2F4B66', bar: '#6C8EAD' },
  crame: { label: 'Cramé', pct: '0-25 %', hint: 'Peu de chances de signer', icon: Ban, bg: '#E9E7E3', fg: '#57534E', bar: '#8A857E' },
};
export const LEVEL_ORDER = ['brulant', 'tres_chaud', 'chaud', 'tiede', 'crame'];
export const HOT_LEVELS = ['brulant', 'tres_chaud', 'chaud'];
export const ThermoIcon = Thermometer;

export const authHeaders = (token) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

export async function bbFetch(token, path, opts = {}) {
  const res = await fetch(`${API}/api${path}`, { ...opts, headers: { ...authHeaders(token), ...(opts.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Erreur serveur');
  return data;
}

export const fmtK = (v) => {
  const n = Number(v || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(2).replace('.', ',')} M€`;
  return `${Math.round(n / 1000)} K€`;
};
export const fmtDate = (s, short = false) => {
  if (!s) return '—';
  const d = new Date(s.length === 10 ? `${s}T12:00:00` : s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', short ? { day: '2-digit', month: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' });
};
export const agoLabel = (days) => (days == null ? '—' : days === 0 ? "aujourd'hui" : `il y a ${days} j`);

export const LevelBadge = ({ level, testId }) => {
  const m = LEVEL_META[level];
  if (!m) return <span className="text-xs text-slate-400">—</span>;
  const Icon = m.icon;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap"
      style={{ background: m.bg, color: m.fg }}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
};

export const AideBadge = ({ days, testId }) => (
  <span data-testid={testId} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: '#FDE3D1', color: '#8A3206' }}>
    Besoin d'aide{days != null ? ` · ${days} j` : ''}
  </span>
);

export const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 ${className}`}>{children}</p>
);

export const Title = ({ children, className = '' }) => (
  <h2 className={`font-[var(--font-display)] uppercase font-bold tracking-tight text-[#1B1C1F] ${className}`} style={{ fontFamily: 'var(--font-display)' }}>{children}</h2>
);

export const Panel = ({ children, className = '', testId }) => (
  <section data-testid={testId} className={`bg-white rounded-2xl p-5 sm:p-6 ${className}`}>{children}</section>
);

export const Scene = ({ children, className = '', testId }) => (
  <section data-testid={testId} className={`rounded-2xl bg-[#25272B] text-white ${className}`}>{children}</section>
);

export const SceneNumber = ({ children, className = '' }) => (
  <p className={`text-5xl sm:text-6xl font-bold leading-none ${className}`} style={{ fontFamily: 'var(--font-display)' }}>{children}</p>
);

export const ProgressBar = ({ value, max, color = '#1B1C1F', track = '#E5E3DE' }) => (
  <div className="h-1.5 rounded-full w-full overflow-hidden" style={{ background: track }}>
    <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, max ? (value / max) * 100 : 0)}%`, background: color }} />
  </div>
);

export const activityColor = (v, obj) => {
  const r = obj ? v / obj : 0;
  return r >= 1 ? '#1E8A4C' : r >= 0.75 ? '#D99A1C' : '#C8372D';
};

export const DECL_STATUS = {
  rendue: { label: 'Rendue', bg: '#DDF1E6', fg: '#1E6B45' },
  rendue_ecart: { label: 'Rendue · écart justifié', bg: '#FBEFD5', fg: '#7A4F0E' },
  en_retard: { label: 'En retard', bg: '#FBE4E2', fg: '#A1281D' },
  brouillon: { label: 'À rendre', bg: '#EEECE8', fg: '#57534E' },
};

export const StatusPill = ({ status, testId }) => {
  const s = DECL_STATUS[status] || DECL_STATUS.brouillon;
  return <span data-testid={testId} className="inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
};

export const fieldCls = 'w-full rounded-lg border border-[#D9D6CF] bg-white px-3 py-2.5 text-sm text-[#1B1C1F] focus:outline-none focus:ring-2 focus:ring-[#F26A1B]/40';
export const btnDark = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25272B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-50';
export const btnOrange = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#F26A1B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d95a10] disabled:opacity-50';
export const btnOutline = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#25272B] bg-white px-4 py-2.5 text-sm font-semibold text-[#1B1C1F] transition-colors hover:bg-[#F3F2EE] disabled:opacity-50';
export const btnDanger = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#C8372D] bg-white px-4 py-2.5 text-sm font-semibold text-[#A1281D] transition-colors hover:bg-[#FBE4E2] disabled:opacity-50';
